import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { Check, Sparkles, Zap, Users, FileText, Bell, Globe, BarChart3, Ship, Smartphone, Monitor, Apple } from "lucide-react";
import ContactForm from "@/components/ContactForm";

/* Landing pública /pro y /precios — accesible sin login. Objetivo: convertir visitas de smileandshine.es en leads Pro. */
export default function Pro() {
  const { lang, setLang, t } = useLang();
  const isES = lang === "es";

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="page-pro">
      {/* Header */}
      <header className="bg-[var(--ink)] text-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <Link to="/login" className="flex items-center gap-3">
            <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-10 rounded-lg" />
            <span className="font-bold text-sm hidden sm:inline">Smile &amp; Shine Cleaning</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-white/10 rounded-full p-1">
              <button onClick={() => setLang("es")}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${lang === "es" ? "bg-white text-[var(--ink)]" : "text-white/80"}`}
                      data-testid="pro-lang-es">🇪🇸 ES</button>
              <button onClick={() => setLang("en")}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${lang === "en" ? "bg-white text-[var(--ink)]" : "text-white/80"}`}
                      data-testid="pro-lang-en">🇬🇧 EN</button>
            </div>
            <Link to="/login" className="hidden sm:inline sns-btn bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2" data-testid="pro-signin">
              {isES ? "Iniciar sesión" : "Sign in"}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-4">
            {isES ? "Mallorca · Premium cleaning" : "Mallorca · Premium cleaning"}
          </p>
          <h1 className="sns-title !text-white text-4xl md:text-6xl leading-tight mb-6 max-w-3xl">
            {isES
              ? "Coordina villas y embarcaciones sin papeles."
              : "Coordinate villas and yachts without paperwork."}
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mb-8">
            {isES
              ? "La app de gestión que tu equipo de limpieza usará todos los días. Programación, horas, fotos, checklist digital y facturación con Holded."
              : "The management app your cleaning team will actually use every day. Scheduling, hours, photos, digital checklist and Holded invoicing."}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#descarga" className="sns-btn sns-btn-accent text-base px-6 py-3.5" data-testid="hero-cta-download">
              {isES ? "Descargar app" : "Download app"}
            </a>
            <a href="#planes" className="sns-btn bg-white/10 hover:bg-white/20 text-white text-base px-6 py-3.5" data-testid="hero-cta-plans">
              {isES ? "Ver planes" : "See plans"}
            </a>
            <a href="#contacto"
               className="sns-btn bg-white/10 hover:bg-white/20 text-white text-base px-6 py-3.5"
               data-testid="hero-cta-demo">
              {isES ? "Solicitar demo" : "Book a demo"}
            </a>
          </div>
        </div>
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full opacity-20"
             style={{ background: "radial-gradient(circle, #00B4D8 0%, transparent 70%)" }} />
      </section>

      {/* Download / Access section */}
      <section id="descarga" className="bg-white border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-5 py-14 md:py-20">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--coral)] mb-3 font-bold">
              {isES ? "Disponible en 3 formatos" : "Available in 3 ways"}
            </p>
            <h2 className="sns-title text-3xl md:text-4xl mb-3">
              {isES ? "Descarga la app o entra desde el navegador" : "Download the app or sign in from your browser"}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {isES
                ? "Tu equipo elige cómo trabajar: móvil Android, iPhone o escritorio. Los datos están sincronizados en tiempo real."
                : "Your team picks how to work: Android, iPhone or desktop. Data syncs in real time."}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* iOS */}
            <a
              href="https://apps.apple.com/es/app/id6803214279"
              target="_blank" rel="noopener noreferrer"
              className="group relative bg-black text-white rounded-2xl p-6 flex items-center gap-4 overflow-hidden hover:scale-[1.02] active:scale-[0.99] transition-transform"
              data-testid="pro-download-ios"
            >
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition">
                <Apple className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-white/60 mb-0.5">
                  {isES ? "Descargar en" : "Download on the"}
                </div>
                <div className="text-xl font-bold leading-tight">App Store</div>
                <div className="text-xs text-white/60 mt-0.5">iPhone &amp; iPad</div>
              </div>
            </a>

            {/* Android */}
            <a
              href="https://play.google.com/store/apps/details?id=es.smileandshine.cleaning"
              target="_blank" rel="noopener noreferrer"
              className="group relative bg-[#0F2A4D] text-white rounded-2xl p-6 flex items-center gap-4 overflow-hidden hover:scale-[1.02] active:scale-[0.99] transition-transform"
              data-testid="pro-download-android"
            >
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition">
                <Smartphone className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-white/60 mb-0.5">
                  {isES ? "Disponible en" : "Get it on"}
                </div>
                <div className="text-xl font-bold leading-tight">Google Play</div>
                <div className="text-xs text-white/60 mt-0.5">{isES ? "Android 7+" : "Android 7+"}</div>
              </div>
            </a>

            {/* Desktop */}
            <Link
              to="/login"
              className="group relative bg-gradient-to-br from-[#FF7A50] to-[#F56038] text-white rounded-2xl p-6 flex items-center gap-4 overflow-hidden hover:scale-[1.02] active:scale-[0.99] transition-transform"
              data-testid="pro-access-desktop"
            >
              <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 group-hover:bg-white/25 transition">
                <Monitor className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-white/80 mb-0.5">
                  {isES ? "Acceder desde" : "Open from"}
                </div>
                <div className="text-xl font-bold leading-tight">
                  {isES ? "Navegador" : "Browser"}
                </div>
                <div className="text-xs text-white/80 mt-0.5">app.smileandshine.es</div>
              </div>
            </Link>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isES
              ? "¿Nueva empresa? "
              : "New here? "}
            <Link to="/register" className="font-semibold text-[var(--coral)] hover:underline">
              {isES ? "Crea tu cuenta gratis — 14 días de prueba Pro." : "Create a free account — 14-day Pro trial."}
            </Link>
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <h2 className="sns-title text-3xl md:text-4xl mb-3 text-center">
          {isES ? "Todo lo que tu equipo necesita" : "Everything your team needs"}
        </h2>
        <p className="text-slate-500 text-center max-w-2xl mx-auto mb-12">
          {isES
            ? "Diseñada específicamente para empresas de limpieza de villas, chalets y embarcaciones en el Mediterráneo."
            : "Built specifically for villa, chalet and yacht cleaning companies in the Mediterranean."}
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard Icon={Ship} title={isES ? "Villas y embarcaciones" : "Villas & yachts"}
            desc={isES ? "Gestiona múltiples propiedades con datos, matrículas, puertos y checklists por zona."
                       : "Manage multiple properties with details, plates, ports and area checklists."} />
          <FeatureCard Icon={Zap} title={isES ? "Check-in con GPS" : "GPS check-in"}
            desc={isES ? "Inicia y finaliza servicios con geolocalización opcional. Control real de horas y pausas."
                       : "Start and finish services with optional GPS. Real hour and break tracking."} />
          <FeatureCard Icon={FileText} title={isES ? "Fotos y firma digital" : "Photos & digital signature"}
            desc={isES ? "Adjunta fotos del servicio con comentarios. Parte de trabajo sin papel."
                       : "Attach service photos with comments. Paperless work reports."} />
          <FeatureCard Icon={BarChart3} title={isES ? "Presupuestos con Holded" : "Holded quotes"}
            desc={isES ? "Emite presupuestos automáticos calculados por horas reales × precio del servicio."
                       : "Auto-generated quotes: real hours × service price."} />
          <FeatureCard Icon={Bell} title={isES ? "Notificaciones push" : "Push notifications"}
            desc={isES ? "El empleado sabe al instante cuándo se le asigna un trabajo o cambia de horario."
                       : "Staff know instantly when they're assigned or when a schedule changes."} />
          <FeatureCard Icon={Globe} title={isES ? "Bilingüe ES/EN" : "Bilingual ES/EN"}
            desc={isES ? "Cambio de idioma con un clic. Perfecto para equipos multinacionales en Mallorca e Ibiza."
                       : "One-click language switch. Perfect for multinational teams in Mallorca and Ibiza."} />
        </div>
      </section>

      {/* Pricing */}
      <section id="planes" className="bg-white py-16 md:py-24 border-y border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="sns-title text-3xl md:text-4xl mb-3 text-center">
            {isES ? "Precios simples y claros" : "Simple, honest pricing"}
          </h2>
          <p className="text-slate-500 text-center mb-12">
            {isES ? "Empieza gratis. Crece cuando lo necesites." : "Start free. Grow when you need to."}
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free */}
            <div className="sns-card p-8" data-testid="plan-card-free">
              <div className="text-sm font-bold text-slate-500 mb-2">FREE</div>
              <div className="sns-title text-4xl mb-1">0€</div>
              <p className="text-slate-500 text-sm mb-6">{isES ? "Para probar la app" : "To try the app"}</p>
              <ul className="space-y-2.5 mb-8 text-sm">
                <PricingRow ok>2 {isES ? "propiedades" : "properties"}</PricingRow>
                <PricingRow ok>1 {isES ? "empleado" : "staff member"}</PricingRow>
                <PricingRow ok>10 {isES ? "mantenimientos/mes" : "jobs/month"}</PricingRow>
                <PricingRow ok>{isES ? "Fotos, checklist y firma" : "Photos, checklist, signature"}</PricingRow>
                <PricingRow>{isES ? "Sin integración Holded" : "No Holded integration"}</PricingRow>
                <PricingRow>{isES ? "Sin exportaciones/reports" : "No exports/reports"}</PricingRow>
              </ul>
              <Link to="/register" className="sns-btn sns-btn-ghost bg-slate-100 w-full py-3" data-testid="cta-free">
                {isES ? "Empezar gratis" : "Start free"}
              </Link>
            </div>

            {/* Pro */}
            <div className="rounded-3xl p-8 text-white relative overflow-hidden shadow-xl"
                 style={{ background: "linear-gradient(135deg, #0F2A4D 0%, #060E1E 100%)" }}
                 data-testid="plan-card-pro">
              <span className="absolute top-4 right-4 bg-[var(--coral)] text-white text-[10px] font-bold px-3 py-1 rounded-full">
                <Sparkles size={10} className="inline -mt-0.5" /> {isES ? "MÁS ELEGIDO" : "MOST POPULAR"}
              </span>
              <div className="text-sm font-bold text-cyan-300 mb-2">PRO</div>
              <div className="flex items-baseline gap-2 mb-1">
              <div className="sns-title !text-white text-4xl">45,99€</div>
                <div className="text-white/60">/{isES ? "mes" : "month"}</div>
              </div>
              <p className="text-cyan-200 text-xs mb-6">
                {isES ? "+ IVA · o 441,50€/año + IVA (20 % de descuento)"
                      : "+ VAT · or 441,50€/year + VAT (20% off)"}
              </p>
              <ul className="space-y-2.5 mb-8 text-sm">
                <PricingRow ok light>{isES ? "Hasta 50 propiedades y embarcaciones, combinadas libremente" : "Up to 50 properties and boats, freely combined"}</PricingRow>
                <PricingRow ok light>{isES ? "Hasta 50 usuarios" : "Up to 50 users"}</PricingRow>
                <PricingRow ok light>{isES ? "Hasta 30 empleados activos a la vez, con su propio acceso y permisos" : "Up to 30 active employees at once, each with their own access and permissions"}</PricingRow>
                <PricingRow ok light>{isES ? "Activa y desactiva usuarios sin coste extra" : "Activate and deactivate users at no extra cost"}</PricingRow>
                <PricingRow ok light>{isES ? "Trabajos e intervenciones sin límite" : "Unlimited jobs and interventions"}</PricingRow>
                <PricingRow ok light>{isES ? "Integración Holded (presupuestos)" : "Holded integration (quotes)"}</PricingRow>
                <PricingRow ok light>{isES ? "Notificaciones push" : "Push notifications"}</PricingRow>
                <PricingRow ok light>{isES ? "Exportaciones e informes" : "Exports & reports"}</PricingRow>
                <PricingRow ok light>{isES ? "Prueba gratis 14 días" : "14-day free trial"}</PricingRow>
              </ul>
              <div className="space-y-2">
                <Link to="/register?plan=pro" className="sns-btn sns-btn-accent w-full py-3 flex items-center justify-center gap-2" data-testid="cta-pro">
                  <Sparkles size={16} /> {isES ? "Empezar prueba de 14 días" : "Start 14-day free trial"}
                </Link>
                <Link to="/login?next=plan" className="block text-center text-cyan-200 text-xs hover:underline" data-testid="cta-pay-now">
                  {isES ? "¿Ya tienes cuenta? Pagar y activar Pro ahora →" : "Already have an account? Pay and activate Pro →"}
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-8">
            {isES ? "¿Equipo de más de 10 personas? Escríbenos a "
                  : "Team of 10+? Contact us at "}
            <a href="mailto:info@smileandshine.es" className="text-[var(--coral)] font-semibold">info@smileandshine.es</a>
            {isES ? " para un plan a medida." : " for a custom plan."}
          </p>
        </div>
      </section>

      {/* CTA final + Formulario de contacto */}
      <section id="contacto" className="max-w-4xl mx-auto px-5 py-16 md:py-24">
        <div className="text-center mb-10">
          <Users size={48} className="mx-auto text-[var(--coral)] mb-4" />
          <h2 className="sns-title text-3xl md:text-4xl mb-3">
            {isES ? "¿Listo para dejar el papel?" : "Ready to go paperless?"}
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            {isES
              ? "Cuéntanos qué gestionas y te enseñamos cómo Smile & Shine encaja con tu equipo. Respondemos en menos de 24 horas."
              : "Tell us what you manage and we'll show you how Smile & Shine fits your team. We reply in less than 24 hours."}
          </p>
        </div>

        <ContactForm lang={lang} source="pro-landing" />

        <div className="text-center mt-8">
          <span className="text-sm text-slate-400">
            {isES ? "¿Prefieres probar la app directamente? " : "Prefer to try the app right now? "}
          </span>
          <Link to="/login" className="text-sm font-semibold text-[var(--coral)] hover:underline" data-testid="cta-final-signup">
            {isES ? "Crear cuenta gratis →" : "Create free account →"}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--ink)] text-white/60 py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div>© {new Date().getFullYear()} Smile &amp; Shine Cleaning · Mallorca</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">{isES ? "Privacidad" : "Privacy"}</Link>
            <a href="mailto:info@smileandshine.es" className="hover:text-white">info@smileandshine.es</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FeatureCard = ({ Icon, title, desc }) => (
  <div className="sns-card p-6">
    <div className="w-11 h-11 rounded-2xl bg-[#FFE6D7] text-[#C2410C] flex items-center justify-center mb-3">
      <Icon size={22} />
    </div>
    <h3 className="sns-title text-lg mb-1">{title}</h3>
    <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
  </div>
);

const PricingRow = ({ ok, light, children }) => (
  <li className={`flex items-start gap-2 ${light ? "!text-white/95" : "text-slate-700"}`}>
    {ok ? <Check size={16} className={`shrink-0 mt-0.5 ${light ? "text-cyan-300" : "text-emerald-500"}`} /> : <span className="text-slate-400 shrink-0">—</span>}
    <span>{children}</span>
  </li>
);
